import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  startTour,
  type Driver,
  type PartnerTourResponse,
  type TourStep,
} from "@dearbill/shared";
import { useApi } from "../auth/useApi";
import { usePartnerMe } from "../auth/usePartnerMe";
import { useOrg } from "../shell/OrgContext";

interface WalkthroughContextValue {
  busy: boolean;
  start: () => void;
  replayRef: RefObject<HTMLButtonElement | null>;
}

const WalkthroughContext = createContext<WalkthroughContextValue | null>(null);

/** The tour preference belongs to one signed-in member and pharmacy. */
export function PharmacyWalkthroughProvider({ children }: { children: ReactNode }): ReactNode {
  const { orgId, res } = useOrg();
  const { me, settled, error } = usePartnerMe();
  const enabled =
    res.orgId === orgId &&
    res.profile.organizationType === "Pharmacy" &&
    settled &&
    !error &&
    me !== null;
  if (!enabled) {
    return <WalkthroughContext.Provider value={null}>{children}</WalkthroughContext.Provider>;
  }
  return <MembershipWalkthrough key={`${me.userId}:${orgId}`}>{children}</MembershipWalkthrough>;
}

function MembershipWalkthrough({ children }: { children: ReactNode }): ReactNode {
  const { orgId, res, isOrgAdmin } = useOrg();
  const api = useApi();
  const location = useLocation();
  const navigate = useNavigate();
  const homePath = `/orgs/${orgId}/home`;
  const endpoint = `/api/partner/orgs/${orgId}/tour`;
  const canRefer = res.status === "active";
  const [requested, setRequested] = useState(false);
  const [running, setRunning] = useState(false);
  const replayRef = useRef<HTMLButtonElement>(null);
  const tourRef = useRef<Driver | null>(null);
  const mounted = useRef(false);
  const [seen, setSeen] = useState<boolean | null>(null);
  const [pageVisible, setPageVisible] = useState(!document.hidden);
  const readRequest = useRef<AbortController | null>(null);
  const writeRequest = useRef<AbortController | null>(null);
  const attempted = useRef(false);
  const interacted = useRef(false);

  const stop = useCallback((): void => {
    const tour = tourRef.current;
    tourRef.current = null;
    tour?.destroy();
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      readRequest.current?.abort();
      writeRequest.current?.abort();
      stop();
    };
  }, [stop]);

  useEffect(() => {
    const request = new AbortController();
    readRequest.current = request;
    api<PartnerTourResponse>(endpoint, { signal: request.signal })
      .then((state) => {
        if (!request.signal.aborted && state.version === 1 && typeof state.seen === "boolean") {
          setSeen(state.seen);
        }
      })
      .catch(() => {
        // Unknown preferences never trigger an automatic interruption.
      });
    return () => request.abort();
  }, [api, endpoint]);

  useEffect(() => {
    interacted.current = false;
    const noteInteraction = (): void => {
      interacted.current = true;
    };
    const visibilityChanged = (): void => setPageVisible(!document.hidden);
    document.addEventListener("pointerdown", noteInteraction, true);
    document.addEventListener("keydown", noteInteraction, true);
    document.addEventListener("visibilitychange", visibilityChanged);
    return () => {
      document.removeEventListener("pointerdown", noteInteraction, true);
      document.removeEventListener("keydown", noteInteraction, true);
      document.removeEventListener("visibilitychange", visibilityChanged);
    };
  }, [location.pathname]);

  const acknowledge = useCallback((): void => {
    if (seen === true) return;
    readRequest.current?.abort();
    setSeen(true);
    const request = new AbortController();
    writeRequest.current = request;
    void api<PartnerTourResponse>(endpoint, {
      method: "PUT",
      body: { version: 1 },
      signal: request.signal,
    }).catch(() => {
      // Keep the tour usable and suppress repeats in this workspace if saving fails.
    });
  }, [api, endpoint, seen]);

  // Navigation or a readiness change invalidates the explanation's targets.
  useEffect(() => {
    return stop;
  }, [location.pathname, canRefer, isOrgAdmin, res.complete, stop]);

  useEffect(() => {
    if (location.pathname !== homePath || running) return;
    const automatic = !requested && seen === false && !attempted.current;
    if (!requested && !automatic) return;
    if (
      automatic &&
      (!pageVisible ||
        interacted.current ||
        document.querySelector('[role="dialog"], .driver-overlay'))
    )
      return;

    const steps: TourStep[] = [
      {
        element: '[data-tour="pharmacy-calculator"]',
        popover: {
          title: "Check payment estimates",
          description:
            "Estimate payments with the Medicare Prescription Payment Plan (M3P). It spreads covered Part D prescription costs across the remaining months of the year. It does not lower yearly drug costs or change coverage. Estimates are not quotes.",
        },
      },
      {
        element: '[data-tour="pharmacy-referral"]',
        popover: {
          title: canRefer ? "Refer with permission" : "Referrals need an active pharmacy",
          description: canRefer
            ? "Check the pharmacy name before sharing a link. With the patient's permission, refer them for review. A referral does not enroll them. The patient or an authorized representative decides whether to apply."
            : "Referrals become available when this pharmacy is active. You can still explore estimates. Check Organization for setup details or ask your organization administrator for help.",
        },
      },
      {
        element: '[data-tour="pharmacy-referrals-tab"]',
        popover: {
          title: "Follow each referral",
          description:
            "Use Referrals to see updates your account can access. Home shows recent referrals, not every case. Review, application submission and enrollment are separate steps. Use Need help? Call for support.",
        },
      },
    ].filter((step) => document.querySelector(step.element) !== null);

    if (steps.length === 0) {
      if (requested) {
        setRequested(false);
        replayRef.current?.focus();
      }
      return;
    }
    attempted.current = true;
    setRequested(false);
    setRunning(true);
    const tour = startTour({
      id: "pharmacy-partner",
      steps,
      remember: false,
      readOnly: true,
      popoverClass: "pharmacy-tour",
      labels: { next: "Next", prev: "Back", done: "Finish tour" },
      onClosed: () => {
        tourRef.current = null;
        if (!mounted.current) return;
        setRunning(false);
        queueMicrotask(() => {
          if (mounted.current && replayRef.current?.isConnected) {
            replayRef.current.focus({ preventScroll: true });
          }
        });
      },
    });
    tourRef.current = tour.isActive() ? tour : null;
    if (tourRef.current) acknowledge();
    else setRunning(false);
  }, [
    requested,
    running,
    seen,
    pageVisible,
    location.pathname,
    homePath,
    canRefer,
    isOrgAdmin,
    acknowledge,
  ]);

  const start = (): void => {
    if (requested || running) return;
    setRequested(true);
    if (location.pathname !== homePath) void navigate(homePath);
  };

  return (
    <WalkthroughContext.Provider value={{ busy: requested || running, start, replayRef }}>
      {children}
    </WalkthroughContext.Provider>
  );
}

/** The optional tour is available from every organization tab. */
export function PharmacyTourButton(): ReactNode {
  const walkthrough = useContext(WalkthroughContext);
  if (!walkthrough) return null;
  return (
    <Button
      ref={walkthrough.replayRef}
      variant="ghost"
      size="md"
      className="min-h-11 max-w-full whitespace-normal border border-white/40 px-3 py-3 text-base text-white [overflow-wrap:anywhere] hover:bg-white/10"
      onClick={walkthrough.start}
      disabled={walkthrough.busy}
    >
      Pharmacy tour
    </Button>
  );
}
